// Typed API client.
//
// `apiFetch(key, input)` is the single entry point: `key` must be one of the
// endpoints declared in RouteTypeMap, `input` is checked against that endpoint's
// request type, and the resolved value is typed as its response. The method and
// path come from `apiRoutes`, so you cannot fetch a path/method pair the server
// doesn't register, nor send the wrong body/query for an endpoint — all of it is
// a compile error. The named helpers below are thin, typed wrappers over it.

import { apiRoutes } from '../lib/apiRoutes';
import type { RouteTypeMap } from '../lib/schemas';
import type {
  SendVerificationRequest,
  SendVerificationApiResponse,
  CheckVerificationRequest,
  CheckVerificationApiResponse,
  LoginPasswordRequest,
  LoginPasswordApiResponse,
  SetPasswordRequest,
  SetPasswordApiResponse,
  UpdateNameRequest,
  UpdateNameApiResponse,
  AvailableSlotsQuery,
  AvailableSlotsApiResponse,
  BookSlotRequest,
  BookSlotApiResponse,
  BookingDensityRequest,
  BookingDensityResponse,
} from '../lib/apiTypes';
import { isSuccessResponse, isErrorResponse } from '../lib/apiTypes';
import { tr } from '../lib/i18n';

export async function apiFetch<K extends keyof RouteTypeMap>(
  key: K,
  input: RouteTypeMap[K]['input']
): Promise<RouteTypeMap[K]['output']> {
  const { method, path } = apiRoutes[key];

  let url: string = path;
  const init: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };

  if (method === 'GET') {
    const params = new URLSearchParams(input as Record<string, string>);
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  } else {
    init.body = JSON.stringify(input);
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (body?.error) {
      throw new Error(body.error);
    }
    // Surfaced to users by callers like login.tsx, so it carries both languages.
    throw new Error(tr(`HTTP error! status: ${response.status}`, `Error HTTP (código ${response.status})`));
  }

  return response.json();
}

export function sendVerification(
  data: SendVerificationRequest
): Promise<SendVerificationApiResponse> {
  return apiFetch('sendVerification', data);
}

export function checkVerification(
  data: CheckVerificationRequest
): Promise<CheckVerificationApiResponse> {
  return apiFetch('checkVerification', data);
}

export function loginPassword(data: LoginPasswordRequest): Promise<LoginPasswordApiResponse> {
  return apiFetch('loginPassword', data);
}

export function setPassword(data: SetPasswordRequest): Promise<SetPasswordApiResponse> {
  return apiFetch('setPassword', data);
}

export function updateName(data: UpdateNameRequest): Promise<UpdateNameApiResponse> {
  return apiFetch('updateName', data);
}

export function getAvailableSlots(
  query: AvailableSlotsQuery
): Promise<AvailableSlotsApiResponse> {
  return apiFetch('getAvailableSlots', query);
}

export function bookSlot(data: BookSlotRequest): Promise<BookSlotApiResponse> {
  return apiFetch('bookSlot', data);
}

export function getBookingDensity(
  query: BookingDensityRequest
): Promise<BookingDensityResponse> {
  return apiFetch('bookingDensity', query);
}

export { isSuccessResponse, isErrorResponse };
